import type { Game } from '@/core/game';
import { tr } from '@/i18n';

/**
 * v1.2 手機操作（像掌機一樣）：
 * - 左下：虛擬搖桿（手指按在左半邊任何地方都可以，搖桿會跟著手指出現）
 * - 右下：Ⓐ 互動（說話、使用、開門、拿東西）——上面會寫現在按了會做什麼；Ⓑ 走／跑切換
 * - 兩指捏合：拉近／拉遠鏡頭
 * 觸控螢幕（或網址加 ?touch=1）才會出現；對話卡、視窗打開時自動藏起來。
 */
export function isTouchMode(): boolean {
  const q = new URLSearchParams(location.search).get('touch');
  if (q === '1') return true;
  if (q === '0') return false;
  return matchMedia('(pointer:coarse)').matches;
}

/** 手機模式時，把說明裡的「按 E」換成「按 Ⓐ」 */
export function keyText(s: string): string {
  if (typeof document === 'undefined' || !document.body?.classList.contains('touch') || !s) return s;
  return s.replace(/按 ?E(?![A-Za-z])/g, '按 Ⓐ').replace(/([Pp])ress E\b/g, '$1ress Ⓐ').replace(/\(R toggles\)\. E to/g, '(R toggles). Ⓐ to');
}

export function setupTouch(g: Game, ui: { isOpen(): boolean; toast(m: string): void; refresh(): void; toggleTasks(open?: boolean): void }): void {
  // v1.2.2 畫面很矮（例如 LINE 內建瀏覽器橫放，只剩 200 多 px 高）：介面整體縮小，不要一個視窗就把畫面塞滿
  const fit = () => {
    const h = innerHeight, w = innerWidth;
    const z = !document.body.classList.contains('touch') ? 1 : h < 250 ? 0.6 : h < 300 ? 0.68 : h < 360 ? 0.78 : h < 430 ? 0.88 : 1;
    const zw = w < 600 ? Math.min(z, 0.8) : z;
    document.documentElement.style.setProperty('--uiz', String(zw));
    document.body.classList.toggle('tiny', zw < 1);
  };
  const apply = () => { document.body.classList.toggle('touch', isTouchMode()); fit(); };
  apply();
  addEventListener('resize', fit); addEventListener('orientationchange', () => setTimeout(fit, 300));
  matchMedia('(pointer:coarse)').addEventListener?.('change', apply);
  const app = document.getElementById('app') ?? document.body;

  const el = (tag: string, cls: string, html = '') => { const e = document.createElement(tag); e.className = cls; if (html) e.innerHTML = html; return e; };
  const root = el('div', 'tc'); root.id = 'touch';
  const zone = el('div', 'tc-zone'); const base = el('div', 'tc-base'); const knob = el('div', 'tc-knob');
  base.append(knob); zone.append(base);
  const act = el('div', 'tc-act');
  const label = el('div', 'tc-label');
  const btnA = el('button', 'tc-a') as HTMLButtonElement; btnA.type = 'button'; btnA.innerHTML = '<b>A</b>'; btnA.setAttribute('aria-label', tr("互動"));
  const btnB = el('button', 'tc-b') as HTMLButtonElement; btnB.type = 'button'; btnB.setAttribute('aria-label', tr("走／跑"));
  const btnC = el('button', 'tc-c') as HTMLButtonElement; btnC.type = 'button'; btnC.textContent = '⇄'; btnC.setAttribute('aria-label', tr("換一個對象"));
  act.append(label, btnB, btnC, btnA);
  root.append(zone, act);
  app.append(root);

  // ---- 搖桿 ----
  const R = 52; let jid: number | null = null; let ox = 0, oy = 0;
  const rest = () => { base.classList.remove('on'); base.style.left = ''; base.style.top = ''; knob.style.transform = ''; g.joy = [0, 0]; };
  zone.addEventListener('pointerdown', e => {
    if (jid !== null || !g.running || g.paused || ui.isOpen()) return;
    e.preventDefault(); jid = e.pointerId; try { zone.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    const r = zone.getBoundingClientRect(); ox = e.clientX; oy = e.clientY;
    base.style.left = `${ox - r.left}px`; base.style.top = `${oy - r.top}px`; base.classList.add('on');
  });
  zone.addEventListener('pointermove', e => {
    if (e.pointerId !== jid) return;
    let dx = e.clientX - ox, dy = e.clientY - oy; const d = Math.hypot(dx, dy);
    if (d > R) { dx = dx / d * R; dy = dy / d * R; }
    knob.style.transform = `translate(${dx}px,${dy}px)`;
    const m = Math.min(1, d / R);
    g.joy = m < 0.18 ? [0, 0] : [dx / R, dy / R];
  });
  const end = (e: PointerEvent) => { if (e.pointerId !== jid) return; jid = null; rest(); };
  zone.addEventListener('pointerup', end); zone.addEventListener('pointercancel', end);

  // ---- Ⓐ Ⓑ ----
  const buzz = () => { try { navigator.vibrate?.(12); } catch { /* ignore */ } };
  btnA.addEventListener('pointerdown', e => { e.preventDefault(); if (!g.running || g.paused || ui.isOpen()) return; buzz(); g.interact(); });
  btnB.addEventListener('pointerdown', e => {
    e.preventDefault(); if (!g.running || ui.isOpen()) return; buzz();
    g.runToggle = !g.runToggle; ui.refresh();
    ui.toast(g.runToggle ? tr("🏃 跑步模式（生產區被品保看到會被警告）") : tr("🚶 走路模式"));
  });

  // ⇄：旁邊有好幾個東西時，換一個互動對象（也可以直接點上面的字）
  const cycle = (e: Event) => { e.preventDefault(); if (!g.running || ui.isOpen()) return; buzz(); g.cycleTarget(); lastLabel = ''; };
  btnC.addEventListener('pointerdown', cycle); label.addEventListener('pointerdown', cycle);

  // ---- 兩指縮放 ----
  const canvas = document.getElementById('c')!;
  const pts = new Map<number, [number, number]>(); let pinch0 = 0, zoom0 = 1;
  canvas.addEventListener('pointerdown', e => { if (e.pointerType !== 'touch') return; pts.set(e.pointerId, [e.clientX, e.clientY]); if (pts.size === 2) { const [a, b] = [...pts.values()]; pinch0 = Math.hypot(a[0] - b[0], a[1] - b[1]); zoom0 = g.stage.zoom; } });
  canvas.addEventListener('pointermove', e => {
    if (!pts.has(e.pointerId)) return; pts.set(e.pointerId, [e.clientX, e.clientY]);
    if (pts.size === 2 && pinch0 > 0) { const [a, b] = [...pts.values()]; const d = Math.hypot(a[0] - b[0], a[1] - b[1]); g.stage.zoom = Math.max(0.55, Math.min(1.7, zoom0 * pinch0 / Math.max(20, d))); }
  });
  const up = (e: PointerEvent) => { pts.delete(e.pointerId); if (pts.size < 2) pinch0 = 0; };
  canvas.addEventListener('pointerup', up); canvas.addEventListener('pointercancel', up);

  // ---- 狀態卡：點一下展開／收起（手機上平常只顯示時間、目標） ----
  document.querySelector('.status')?.addEventListener('click', e => {
    if (!document.body.classList.contains('touch')) return;
    if ((e.target as HTMLElement).closest('button')) return;
    document.body.classList.toggle('status-open');
  });

  // ---- 按鈕上的字（現在按 Ⓐ 會做什麼）、開視窗時藏起來 ----
  let lastLabel = '';
  let zoomSet = false;
  const tick = () => {
    const on = document.body.classList.contains('touch');
    const hide = !on || !g.running || ui.isOpen() || !!document.querySelector('.ww');
    root.hidden = hide;
    if (hide && jid !== null) { jid = null; rest(); }
    if (!hide) {
      if (!zoomSet) { zoomSet = true; if (g.stage.zoom === 1) g.stage.zoom = 0.82; if (innerHeight < 520) ui.toggleTasks(false); }   // 手機畫面小：鏡頭預設拉近一點、任務欄先收起來
      const n = g.nearest(); const d = g.day;
      const txt = n ? n.label : d?.holding ? (d.holding.kind === 'cart' ? tr("放下烏龜車") : tr("🧺 放下")) : '';
      if (txt !== lastLabel) { lastLabel = txt; label.textContent = txt; label.hidden = !txt; btnA.classList.toggle('ready', !!txt); }
      btnC.hidden = g.targetCount() < 2; label.classList.toggle('multi', !btnC.hidden);
      btnB.textContent = g.runToggle ? '🏃' : '🚶';
      btnB.classList.toggle('on', g.runToggle);
    }
    setTimeout(tick, 120);
  };
  tick();
}
